/**
 * `/admin/$orgUuid` — org reconciliation detail.
 *
 * One screen reconciling an org's state across every system that holds a
 * copy: DB plan + plan_limits, live Polar subscription, credit balance, and
 * the recent ledger. The read that replaces cross-console archaeology.
 */
import { AdminInvitesTab } from '@/components/admin/AdminInvitesTab';
import { AdminSitesTab } from '@/components/admin/AdminSitesTab';
import { DeleteOrgDialog } from '@/components/admin/DeleteOrgDialog';
import { ImpersonateButton } from '@/components/shell/ImpersonateButton';
import { Island } from '@/components/shell/Island';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  type AdminOrgDetail,
  useAdminOrg,
  useGrantCreditsMutation,
  useRenameOrgMutation,
  useResyncKvMutation,
} from '@/lib/queries/admin';
import { createFileRoute } from '@tanstack/react-router';
import { type ReactElement, type ReactNode, Suspense, lazy, useState } from 'react';

// Lazy so recharts is code-split out of the admin route's main chunk.
const AdminOrgCharts = lazy(() => import('@/components/admin/AdminOrgCharts'));

export const Route = createFileRoute('/_app/admin/$orgUuid')({
  component: AdminOrgDetailPage,
});

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function Section({
  title,
  action,
  children,
}: { title: string; action?: ReactNode; children: ReactNode }): ReactElement {
  return (
    <section className="rounded-md border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="font-medium text-sm text-[var(--color-ink)]">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-[11px] text-[var(--color-mute)] uppercase tracking-wide">{label}</dt>
      <dd className="text-sm text-[var(--color-ink)]">{children ?? '—'}</dd>
    </div>
  );
}

/**
 * Same normalisation the API applies, mirrored here only so the slug field
 * previews what will actually be stored while you type. The server is the
 * authority — this never gates submission.
 */
function slugPreview(value: string): string {
  const slug = value
    .normalize('NFKD')
    .replace(/\p{Mn}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .slice(0, 48)
    .replace(/^-+|-+$/g, '');
  return slug || 'partner';
}

/**
 * Rename an org from the staff surface.
 *
 * Both fields write to Clerk — it owns the display name outright and is the
 * authority on the read path for the slug. The slug field is forgiving: type
 * a display name and it normalises, with a live preview of what lands, so
 * staff don't have to hand-slug anything.
 *
 * Editing the name pre-fills the slug from it, but only while the slug hasn't
 * been touched — an org whose slug was deliberately set to something other
 * than its name keeps it when the name is corrected.
 */
function RenameOrgForm({
  orgUuid,
  currentName,
  currentSlug,
  onDone,
}: {
  orgUuid: string;
  currentName: string | null;
  currentSlug: string;
  onDone: () => void;
}): ReactElement {
  const [name, setName] = useState(currentName ?? '');
  const [slug, setSlug] = useState(currentSlug);
  const [slugTouched, setSlugTouched] = useState(false);
  const rename = useRenameOrgMutation(orgUuid);

  const nextSlug = slugPreview(slug);
  const changed = name.trim() !== (currentName ?? '') || nextSlug !== currentSlug;
  const valid = name.trim().length > 0 && slug.trim().length > 0;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || !changed || rename.isPending) return;
        rename.mutate(
          {
            ...(name.trim() === (currentName ?? '') ? {} : { name: name.trim() }),
            ...(nextSlug === currentSlug ? {} : { slug }),
          },
          { onSuccess: onDone },
        );
      }}
      className="flex flex-col gap-3"
    >
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-1 flex-col gap-1">
          <span className="text-[11px] text-[var(--color-mute)] uppercase tracking-wide">
            Display name
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (!slugTouched) setSlug(e.target.value);
            }}
            placeholder="Acme Inc"
            className="h-8 w-full min-w-48 rounded-md border border-[var(--color-line)] bg-[var(--color-paper)] px-2 text-sm outline-none focus:border-[var(--color-line-strong)]"
          />
        </label>
        <label className="flex flex-1 flex-col gap-1">
          <span className="text-[11px] text-[var(--color-mute)] uppercase tracking-wide">Slug</span>
          <input
            type="text"
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value);
              setSlugTouched(true);
            }}
            placeholder="acme-inc"
            className="h-8 w-full min-w-48 rounded-md border border-[var(--color-line)] bg-[var(--color-paper)] px-2 font-mono text-sm outline-none focus:border-[var(--color-line-strong)]"
          />
        </label>
        <button
          type="submit"
          disabled={!valid || !changed || rename.isPending}
          className="h-8 rounded-md bg-[var(--color-ink)] px-3 font-medium text-[var(--color-paper)] text-xs hover:opacity-90 disabled:opacity-40"
        >
          {rename.isPending ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="h-8 rounded-md border border-[var(--color-line)] px-3 text-xs hover:bg-[var(--color-paper-alt)]"
        >
          Cancel
        </button>
      </div>

      <p className="text-xs text-[var(--color-mute)]">
        Saves as <span className="font-mono text-[var(--color-ink-soft)]">{nextSlug}</span>
        {nextSlug === currentSlug ? ' (unchanged)' : ''}. The display name is what the customer sees
        in their own sidebar.
      </p>

      {rename.error ? (
        <span className="text-xs text-[var(--color-danger)]">
          {String((rename.error as Error)?.message ?? rename.error)}
        </span>
      ) : null}
    </form>
  );
}

function creditsView(credits: AdminOrgDetail['credits']): ReactElement {
  if (!credits) {
    return (
      <p className="text-sm text-[var(--color-mute)]">
        No wallet row — this org has never been granted credits.
      </p>
    );
  }
  return (
    <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
      <Field label="Balance">
        <span className="font-medium tabular-nums">{credits.balance.toLocaleString()}</span>
      </Field>
      <Field label="Cycle / Top-up">
        <span className="tabular-nums">
          {credits.cycle_balance.toLocaleString()} / {credits.topup_balance.toLocaleString()}
        </span>
      </Field>
      <Field label="Spent this period">
        <span className="tabular-nums">{credits.credits_spent_this_period.toLocaleString()}</span>
      </Field>
      <Field label="Cycle expires">{fmtDateTime(credits.cycle_expires_at)}</Field>
      <Field label="Budget">
        {credits.budget_mode === 'off'
          ? 'off'
          : `${credits.budget_mode} · ${credits.budget_credits?.toLocaleString() ?? '—'}`}
      </Field>
      <Field label="Overage">{credits.overage_enabled ? 'enabled' : 'off'}</Field>
    </dl>
  );
}

function ResyncButton({ orgUuid }: { orgUuid: string }): ReactElement {
  const resync = useResyncKvMutation(orgUuid);
  return (
    <button
      type="button"
      onClick={() => resync.mutate()}
      disabled={resync.isPending}
      className="h-7 rounded-md border border-[var(--color-line)] px-2.5 text-xs hover:bg-[var(--color-paper-alt)] disabled:opacity-50"
    >
      {resync.isPending
        ? 'Resyncing…'
        : resync.isSuccess
          ? 'Pixel KV resynced ✓'
          : resync.error
            ? 'Resync failed — retry'
            : 'Resync pixel KV'}
    </button>
  );
}

function GrantForm({ orgUuid }: { orgUuid: string }): ReactElement {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const grant = useGrantCreditsMutation(orgUuid);
  const amt = Number(amount);
  const valid = Number.isInteger(amt) && amt > 0 && reason.trim().length > 0;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || grant.isPending) return;
        grant.mutate(
          { amount: amt, reason: reason.trim() },
          {
            onSuccess: () => {
              setAmount('');
              setReason('');
            },
          },
        );
      }}
      className="mt-4 flex flex-wrap items-end gap-2 border-t border-[var(--color-line-soft)] pt-4"
    >
      <label className="flex flex-col gap-1">
        <span className="text-[11px] text-[var(--color-mute)] uppercase tracking-wide">Amount</span>
        <input
          type="number"
          min={1}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="500"
          className="h-8 w-28 rounded-md border border-[var(--color-line)] bg-[var(--color-paper)] px-2 text-sm tabular-nums outline-none focus:border-[var(--color-line-strong)]"
        />
      </label>
      <label className="flex flex-1 flex-col gap-1">
        <span className="text-[11px] text-[var(--color-mute)] uppercase tracking-wide">Reason</span>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. goodwill comp for the 07-14 outage"
          className="h-8 w-full min-w-48 rounded-md border border-[var(--color-line)] bg-[var(--color-paper)] px-2 text-sm outline-none focus:border-[var(--color-line-strong)]"
        />
      </label>
      <button
        type="submit"
        disabled={!valid || grant.isPending}
        className="h-8 rounded-md bg-[var(--color-ink)] px-3 font-medium text-[var(--color-paper)] text-xs hover:opacity-90 disabled:opacity-40"
      >
        {grant.isPending ? 'Granting…' : 'Grant credits'}
      </button>
      {grant.isSuccess ? (
        <span className="text-xs text-[var(--color-mute)]">
          Granted — new balance {grant.data.new_balance.toLocaleString()}.
        </span>
      ) : null}
      {grant.error ? (
        <span className="text-xs text-[var(--color-danger)]">
          {String((grant.error as Error)?.message ?? grant.error)}
        </span>
      ) : null}
    </form>
  );
}

function AdminOrgDetailPage(): ReactElement {
  const { orgUuid } = Route.useParams();
  const q = useAdminOrg(orgUuid);
  const d = q.data;
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Breadcrumb prefers the display name — the slug is the fallback identity,
  // and the whole point of renaming is to stop reading `org-2xyz…`.
  const breadcrumb = [
    { label: 'Admin', href: '/admin' as const },
    { label: d?.org.name || d?.org.slug || orgUuid },
  ];

  return (
    <Island
      breadcrumb={breadcrumb}
      actions={
        <>
          <ImpersonateButton orgUuid={orgUuid} />
          <ResyncButton orgUuid={orgUuid} />
          {d ? (
            <button
              type="button"
              onClick={() => setDeleting(true)}
              className="h-7 rounded-md border border-[var(--color-danger)]/40 px-2.5 text-[var(--color-danger)] text-xs hover:bg-[var(--color-danger)]/10"
            >
              Delete org
            </button>
          ) : null}
        </>
      }
    >
      {q.isLoading ? (
        <p className="py-8 text-center text-sm text-[var(--color-mute)]">Loading…</p>
      ) : q.error ? (
        <p className="py-8 text-center text-sm text-[var(--color-danger)]">
          {String((q.error as Error)?.message ?? q.error)}
        </p>
      ) : d ? (
        <>
          <DeleteOrgDialog
            orgUuid={orgUuid}
            slug={d.org.slug}
            name={d.org.name}
            open={deleting}
            onOpenChange={setDeleting}
          />
          <Tabs defaultValue="overview">
            <TabsList variant="underline">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="credits">Credits</TabsTrigger>
              <TabsTrigger value="sites">Sites</TabsTrigger>
              <TabsTrigger value="invites">Invites</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="flex flex-col gap-4 pt-4">
              <Section
                title="Organisation"
                action={
                  renaming ? null : (
                    <button
                      type="button"
                      onClick={() => setRenaming(true)}
                      className="h-7 rounded-md border border-[var(--color-line)] px-2.5 text-xs hover:bg-[var(--color-paper-alt)]"
                    >
                      Rename
                    </button>
                  )
                }
              >
                {renaming ? (
                  <RenameOrgForm
                    orgUuid={orgUuid}
                    currentName={d.org.name}
                    currentSlug={d.org.slug}
                    onDone={() => setRenaming(false)}
                  />
                ) : (
                  <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <Field label="Name">
                      {d.org.name ?? (
                        <span className="text-[var(--color-mute)]">unavailable (Clerk)</span>
                      )}
                    </Field>
                    <Field label="Slug">
                      <span className="font-mono text-xs">{d.org.slug}</span>
                    </Field>
                    <Field label="Plan">
                      {d.org.plan ? (
                        <span className="capitalize">{d.org.plan}</span>
                      ) : (
                        <span className="text-[var(--color-mute)]">none</span>
                      )}
                    </Field>
                    <Field label="Created">{fmtDateTime(d.org.created_at)}</Field>
                    <Field label="Status">
                      {d.org.deleted_at ? (
                        <span className="text-[var(--color-danger)]">deleted</span>
                      ) : (
                        'active'
                      )}
                    </Field>
                    <Field label="UUID">
                      <span className="font-mono text-xs">{d.org.uuid}</span>
                    </Field>
                  </dl>
                )}
              </Section>

              <Section title="Activity">
                <Suspense
                  fallback={
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      <div className="h-[184px] rounded-md border border-[var(--color-line)] bg-[var(--color-paper-alt)]" />
                      <div className="h-[184px] rounded-md border border-[var(--color-line)] bg-[var(--color-paper-alt)]" />
                      <div className="h-[184px] rounded-md border border-[var(--color-line)] bg-[var(--color-paper-alt)]" />
                    </div>
                  }
                >
                  <AdminOrgCharts daily={d.daily} events={d.events} />
                </Suspense>
              </Section>

              <Section title="Billing (Polar)">
                <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                  <Field label="Live status">{d.polar.status ?? 'no subscription'}</Field>
                  <Field label="Current period end">
                    {fmtDateTime(d.polar.current_period_end)}
                  </Field>
                  <Field label="Cancels at period end">
                    {d.polar.cancel_at_period_end == null
                      ? '—'
                      : d.polar.cancel_at_period_end
                        ? 'yes'
                        : 'no'}
                  </Field>
                  <Field label="Subscription id">
                    <span className="font-mono text-xs">{d.polar.subscription_id ?? '—'}</span>
                  </Field>
                  <Field label="Customer id">
                    <span className="font-mono text-xs">{d.polar.customer_id ?? '—'}</span>
                  </Field>
                </dl>
              </Section>

              <Section title="Plan limits (DB)">
                <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {Object.entries(d.plan_limits).map(([k, v]) => (
                    <Field key={k} label={k}>
                      <span className="tabular-nums">{String(v)}</span>
                    </Field>
                  ))}
                </dl>
              </Section>
            </TabsContent>

            <TabsContent value="credits" className="flex flex-col gap-4 pt-4">
              <Section title="Credits">
                {creditsView(d.credits)}
                <GrantForm orgUuid={d.org.uuid} />
              </Section>

              <Section title="Recent ledger">
                {d.ledger.length === 0 ? (
                  <p className="text-sm text-[var(--color-mute)]">No ledger entries.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[560px] border-collapse text-sm">
                      <thead>
                        <tr className="border-b border-[var(--color-line)] text-left text-xs text-[var(--color-mute)]">
                          <th className="py-1.5 pr-4 font-medium">When</th>
                          <th className="py-1.5 pr-4 font-medium">Kind</th>
                          <th className="py-1.5 pr-4 font-medium">Action</th>
                          <th className="py-1.5 pr-4 text-right font-medium">Δ</th>
                          <th className="py-1.5 pr-4 text-right font-medium">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.ledger.map((e) => (
                          <tr
                            key={e.entry_uuid}
                            className="border-b border-[var(--color-line-soft)]"
                          >
                            <td className="py-1.5 pr-4 text-xs text-[var(--color-mute)]">
                              {fmtDateTime(e.created_at)}
                            </td>
                            <td className="py-1.5 pr-4 text-xs">{e.kind}</td>
                            <td className="py-1.5 pr-4 text-xs text-[var(--color-mute)]">
                              {e.action ?? '—'}
                            </td>
                            <td
                              className={`py-1.5 pr-4 text-right tabular-nums ${e.delta < 0 ? 'text-[var(--color-danger)]' : 'text-[var(--color-ink)]'}`}
                            >
                              {e.delta > 0 ? `+${e.delta}` : e.delta}
                            </td>
                            <td className="py-1.5 pr-4 text-right tabular-nums text-[var(--color-mute)]">
                              {e.balance_after.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>
            </TabsContent>

            <TabsContent value="sites" className="pt-4">
              <AdminSitesTab orgUuid={orgUuid} />
            </TabsContent>

            <TabsContent value="invites" className="pt-4">
              <AdminInvitesTab orgUuid={orgUuid} />
            </TabsContent>
          </Tabs>
        </>
      ) : null}
    </Island>
  );
}
